
import React, { useState } from 'react';
import './PostCard.css';
import { Post, useApp } from '../../context/AppContext';
import TipModal from '../TipModal/TipModal';
import UnlockModal from '../UnlockModal/UnlockModal';

interface PostCardProps {
  post: Post;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { adminUser, currentUser, likePost, savePost } = useApp();
  const [showTipModal, setShowTipModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const isLiked = post.likedBy.includes(currentUser.id);
  const isSaved = post.savedBy.includes(currentUser.id);
  const canView = !post.isLocked || currentUser.isSubscribed;

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const images = post.mediaUrls || (post.mediaUrl ? [post.mediaUrl] : []);

  return (
    <article className="post-card animate-fade-in">
      <div className="post-header">
        <div className="post-avatar-container">
          <img src={adminUser.avatar} alt={adminUser.displayName} className="post-avatar" />
          <div className="avatar-online-dot" />
        </div>
        <div className="post-user-info">
          <div className="post-user-name">
            {adminUser.displayName}
            <svg className="verified-badge" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <div className="post-meta">
            <span className="post-time">{formatTime(post.timestamp)}</span>
            {post.isLocked && !canView && <span className="locked-badge">🔒 Premium</span>}
          </div>
        </div>
        <button className="post-more-btn">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="6" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="12" cy="18" r="2"/>
          </svg>
        </button>
      </div>

      {post.content && <p className="post-content">{post.content}</p>}

      {images.length > 0 && (
        <div className={`post-media ${!canView ? 'locked' : ''}`}>
          <img src={images[imageIndex]} alt="Post media" />
          {images.length > 1 && canView && (
            <>
              <div className="media-indicators">
                {images.map((_, i) => (
                  <span key={i} className={`indicator ${i === imageIndex ? 'active' : ''}`} />
                ))}
              </div>
              <button className="media-nav prev" onClick={() => setImageIndex(i => Math.max(0, i - 1))}>‹</button>
              <button className="media-nav next" onClick={() => setImageIndex(i => Math.min(images.length - 1, i + 1))}>›</button>
            </>
          )}
          {!canView && (
            <div className="locked-overlay" onClick={() => setShowUnlockModal(true)}>
              <div className="locked-content">
                <div className="lock-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                </div>
                <span className="locked-text">Unlock for ${post.price}</span>
                <button className="unlock-btn">Unlock Now</button>
              </div>
            </div>
          )}
          {post.mediaType === 'video' && canView && (
            <div className="video-indicator">
              <div className="play-button">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="post-actions">
        <button className={`action-btn ${isLiked ? 'liked' : ''}`} onClick={() => likePost(post.id)}>
          <svg viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
          </svg>
          <span>{formatNumber(post.likes)}</span>
        </button>

        <button className="action-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
          </svg>
          <span>{formatNumber(post.comments)}</span>
        </button>

        <button className="action-btn tip-btn" onClick={() => setShowTipModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
          </svg>
          <span>Tip</span>
        </button>

        <button className={`action-btn save-btn ${isSaved ? 'saved' : ''}`} onClick={() => savePost(post.id)}>
          <svg viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
          </svg>
        </button>
      </div>

      {showTipModal && <TipModal onClose={() => setShowTipModal(false)} />}
      {showUnlockModal && <UnlockModal post={post} onClose={() => setShowUnlockModal(false)} />}
    </article>
  );
};

export default PostCard;
